import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AcknowledgedHistoryWrites } from "../helpers/acknowledgedHistoryWrites";
import { historyTimestamp } from "../helpers/historyTimestamp";
import { recoveryErrorMessage } from "../../utils/recovery-error";
import {
  fetchPrivateHistoryPage,
  storePrivateHistoryEntry,
} from "../../api/private-data-storage";
import { formatMpkHex, hashMpkHex } from "../../utils/format";
import { fetchPrivateFeeHistoryPage } from "../../joinsplit-v2/privateFeeHistory";
import type { BaseHistoryEntry } from "../types";
import {
  loadBaseHistory,
  loadEncryptedBaseHistory,
  saveBaseHistory,
  saveEncryptedBaseHistory,
} from "../helpers/storage";
import {
  formatInboxPublicKeyHex,
  getInboxSecretKeyCandidates,
  getInboxViewingKeyCandidates,
} from "../../utils/inbox-crypto";

const BASE_HISTORY_TYPES = new Set<BaseHistoryEntry["type"]>([
  "deposit",
  "transfer",
  "withdraw",
  "receive",
  "swap",
  "fee",
  "bridge",
]);

function normalizeHistoryType(value: unknown): BaseHistoryEntry["type"] {
  return typeof value === "string" &&
    BASE_HISTORY_TYPES.has(value as BaseHistoryEntry["type"])
    ? (value as BaseHistoryEntry["type"])
    : "transfer";
}

function normalizeHistoryText(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim().length > 0
    ? value
    : fallback;
}

interface UseBaseHistorySyncParams {
  address: string;
  spendingKeys: any;
  notesApiUrl: string;
  cacheSecret: string;
  authHeadersForPath: (path: string) => Promise<Record<string, string>>;
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
  ) => void;
}

type PendingHistoryEntry = Omit<BaseHistoryEntry, "id" | "timestamp"> & {
  timestamp?: number;
};

function historyEntryKey(entry: Omit<BaseHistoryEntry, "id" | "timestamp">) {
  return (
    entry.eventId ||
    `${(entry.txHash || "").toLowerCase()}|${entry.type}|${entry.amount}|${entry.symbol}`
  );
}

function historyWriteKey(entry: Omit<BaseHistoryEntry, "id" | "timestamp">) {
  return JSON.stringify([historyEntryKey(entry), entry.memo ?? ""]);
}

export function useBaseHistorySync({
  address,
  spendingKeys,
  notesApiUrl,
  cacheSecret,
  authHeadersForPath,
  addLog,
}: UseBaseHistorySyncParams) {
  const [historyEntries, setHistoryEntries] = useState<BaseHistoryEntry[]>([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyPageLoading, setHistoryPageLoading] = useState(false);
  const [historyPageError, setHistoryPageError] = useState<string | null>(null);
  const paging = useMemo(
    () => ({
      busy: false,
      loaded: false,
      normal: null as string | null,
      fee: null as number | null,
    }),
    [address, spendingKeys, notesApiUrl],
  );
  const currentPaging = useRef(paging);
  currentPaging.current = paging;
  useEffect(() => {
    setHistoryHasMore(false);
    setHistoryPageLoading(false);
    setHistoryPageError(null);
  }, [paging]);
  const normalizedAddress = address.trim().toLowerCase();
  // A wallet/key/backend switch must not inherit another scope's ACKs.
  const historyWrites = useMemo(
    () => new AcknowledgedHistoryWrites(),
    [normalizedAddress, spendingKeys, notesApiUrl],
  );
  const latestOwnerRef = useRef({
    address: normalizedAddress,
    spendingKeys,
  });

  useEffect(() => {
    latestOwnerRef.current = {
      address: normalizedAddress,
      spendingKeys,
    };
  }, [normalizedAddress, spendingKeys]);

  useEffect(() => {
    if (!normalizedAddress) {
      setHistoryEntries([]);
      return;
    }
    setHistoryEntries([]);
    loadBaseHistory(normalizedAddress);
    if (!cacheSecret) {
      setHistoryEntries([]);
      return;
    }
    let cancelled = false;
    void loadEncryptedBaseHistory(normalizedAddress, cacheSecret).then(
      (entries) => {
        if (!cancelled && !paging.loaded)
          setHistoryEntries(
            entries
              .map((entry) => ({
                ...entry,
                timestamp: historyTimestamp(entry.timestamp),
              }))
              .sort((a, b) => b.timestamp - a.timestamp),
          );
      },
    );
    return () => {
      cancelled = true;
    };
  }, [normalizedAddress, cacheSecret, paging]);

  const readHistoryPage = useCallback(
    async (append: boolean) => {
      if (
        !normalizedAddress ||
        !spendingKeys ||
        paging.busy ||
        (append &&
          paging.loaded &&
          paging.normal === null &&
          paging.fee === null)
      )
        return;
      paging.busy = true;
      setHistoryPageLoading(true);
      setHistoryPageError(null);
      try {
        const mpkHex = formatMpkHex(spendingKeys.mpk);
        const recipientKey = await hashMpkHex(mpkHex);
        const headers = await authHeadersForPath("/api/v1/history");
        const [normal, fees] = await Promise.all([
          append && paging.normal === null
            ? Promise.resolve({ entries: [], nextCursor: null })
            : fetchPrivateHistoryPage({
                baseUrl: notesApiUrl,
                recipientKey,
                headers,
                before: append ? paging.normal : null,
                decryptContext: {
                  viewingKeyHex: getInboxViewingKeyCandidates(
                    spendingKeys.spendingKey,
                  ),
                  legacyMpkHex: mpkHex,
                  inboxSecretKey: getInboxSecretKeyCandidates(
                    spendingKeys.spendingKey,
                  ),
                },
              }),
          append && paging.fee === null
            ? Promise.resolve({ entries: [], nextCursor: null })
            : fetchPrivateFeeHistoryPage(
                spendingKeys,
                append ? paging.fee : null,
                headers,
              ),
        ]);
        if (currentPaging.current !== paging) return;
        const mapped: BaseHistoryEntry[] = normal.entries.map(
          (entry, index) => ({
            id: Date.now() + index,
            type: normalizeHistoryType(entry.type),
            amount: normalizeHistoryText(entry.amount, "0"),
            symbol: normalizeHistoryText(
              entry.symbol,
              normalizeHistoryText(entry.token, "ETH"),
            ),
            memo: typeof entry.memo === "string" ? entry.memo : undefined,
            txHash: [
              entry.txHash,
              entry.tx_hash,
              entry.transactionHash,
              entry.transaction_hash,
            ].find((value): value is string => typeof value === "string"),
            timestamp: historyTimestamp(entry.timestamp),
          }),
        );
        for (const entry of mapped)
          historyWrites.acknowledge(historyWriteKey(entry));
        mapped.push(...fees.entries);
        paging.normal = normal.nextCursor;
        paging.fee = fees.nextCursor;
        paging.loaded = true;
        setHistoryHasMore(paging.normal !== null || paging.fee !== null);
        setHistoryEntries((prev) => {
          const previous = new Map(
            prev.map((entry) => [historyEntryKey(entry), entry]),
          );
          const byKey = new Map<string, BaseHistoryEntry>();
          // Preserve pending local actions on refresh; append keeps all pages.
          for (const entry of prev)
            if (append || Date.now() - entry.timestamp < 30 * 60 * 1000)
              byKey.set(historyEntryKey(entry), entry);
          for (const entry of mapped) {
            const key = historyEntryKey(entry);
            byKey.set(key, {
              ...entry,
              memo: entry.memo ?? previous.get(key)?.memo,
            });
          }
          const next = [...byKey.values()].sort(
            (a, b) => b.timestamp - a.timestamp,
          );
          saveBaseHistory(normalizedAddress, next);
          if (cacheSecret)
            void saveEncryptedBaseHistory(normalizedAddress, next, cacheSecret);
          return next;
        });
      } catch (error) {
        if (currentPaging.current !== paging) return;
        setHistoryPageError("Activity could not be loaded. Please try again.");
        addLog(
          `History sync failed: ${recoveryErrorMessage(error)}`,
          "warning",
        );
      } finally {
        paging.busy = false;
        if (currentPaging.current === paging) setHistoryPageLoading(false);
      }
    },
    [
      normalizedAddress,
      spendingKeys,
      notesApiUrl,
      authHeadersForPath,
      cacheSecret,
      historyWrites,
      paging,
      addLog,
    ],
  );
  const syncHistoryEntries = useCallback(
    () => readHistoryPage(false),
    [readHistoryPage],
  );
  const loadMoreHistory = useCallback(
    () => readHistoryPage(true),
    [readHistoryPage],
  );

  const pushHistoryEntry = useCallback(
    (entry: Omit<BaseHistoryEntry, "id" | "timestamp">) => {
      const requestAddress = normalizedAddress;
      setHistoryEntries((prev) => {
        if (
          !requestAddress ||
          latestOwnerRef.current.address !== requestAddress
        ) {
          return prev;
        }
        const key = historyEntryKey(entry);
        const nextEntry = {
          id: Date.now(),
          timestamp: Date.now(),
          ...entry,
        };
        const next: BaseHistoryEntry[] = [
          nextEntry,
          ...prev.filter((existing) => historyEntryKey(existing) !== key),
        ];
        saveBaseHistory(requestAddress, next);
        if (cacheSecret) {
          void saveEncryptedBaseHistory(requestAddress, next, cacheSecret);
        }
        return next;
      });
    },
    [normalizedAddress, cacheSecret],
  );

  const mergeHistoryEntries = useCallback(
    (entries: PendingHistoryEntry[]) => {
      if (entries.length === 0) return;
      const requestAddress = normalizedAddress;
      setHistoryEntries((prev) => {
        if (
          !requestAddress ||
          latestOwnerRef.current.address !== requestAddress
        ) {
          return prev;
        }
        const nextByKey = new Map<string, BaseHistoryEntry>();
        for (const entry of prev) {
          nextByKey.set(historyEntryKey(entry), entry);
        }
        for (const entry of entries) {
          const timestamp = historyTimestamp(entry.timestamp);
          const normalized: BaseHistoryEntry = {
            id: timestamp,
            timestamp,
            type: entry.type,
            eventId: entry.eventId,
            feeRole: entry.feeRole,
            amount: entry.amount,
            symbol: entry.symbol,
            memo: entry.memo,
            txHash: entry.txHash,
          };
          const key = historyEntryKey(normalized);
          const previous = nextByKey.get(key);
          nextByKey.set(key, {
            ...normalized,
            id: previous?.id ?? normalized.id,
            memo: normalized.memo ?? previous?.memo,
          });
        }
        const next = Array.from(nextByKey.values()).sort(
          (a, b) => b.timestamp - a.timestamp,
        );
        saveBaseHistory(requestAddress, next);
        if (cacheSecret) {
          void saveEncryptedBaseHistory(requestAddress, next, cacheSecret);
        }
        return next;
      });
    },
    [normalizedAddress, cacheSecret],
  );

  const storeHistoryEvent = useCallback(
    async (entry: Omit<BaseHistoryEntry, "id" | "timestamp">) => {
      const requestAddress = normalizedAddress;
      const requestSpendingKeys = spendingKeys;
      if (!requestAddress || !requestSpendingKeys) return;
      if (
        latestOwnerRef.current.address !== requestAddress ||
        latestOwnerRef.current.spendingKeys !== requestSpendingKeys
      ) {
        return;
      }
      try {
        await historyWrites.run(historyWriteKey(entry), async () => {
          const recipient = await hashMpkHex(
            formatMpkHex(requestSpendingKeys.mpk),
          );
          const authHeaders = await authHeadersForPath("/api/v1/history/store");
          if (
            latestOwnerRef.current.address !== requestAddress ||
            latestOwnerRef.current.spendingKeys !== requestSpendingKeys
          ) {
            throw new Error("Wallet changed during history write");
          }
          await storePrivateHistoryEntry({
            baseUrl: notesApiUrl,
            headers: authHeaders,
            entry: {
              recipient,
              type: entry.type,
              amount: entry.amount,
              // Persist the UI symbol/route label so synced history can render
              // the same human-readable swap/deposit/withdraw token context.
              token: entry.symbol,
              memo: entry.memo,
              txHash: entry.txHash || "",
              direction: entry.type === "receive" ? "in" : "out",
            },
            encryptTarget: {
              inboxPublicKeyHex: formatInboxPublicKeyHex(
                requestSpendingKeys.spendingKey,
              ),
            },
          });
        });
      } catch (err) {
        if (
          latestOwnerRef.current.address !== requestAddress ||
          latestOwnerRef.current.spendingKeys !== requestSpendingKeys
        ) {
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        addLog(`History store failed: ${msg}`, "warning");
      }
    },
    [
      normalizedAddress,
      spendingKeys,
      authHeadersForPath,
      notesApiUrl,
      addLog,
      historyWrites,
    ],
  );

  return {
    historyEntries,
    historyHasMore,
    historyPageLoading,
    historyPageError,
    loadMoreHistory,
    pushHistoryEntry,
    mergeHistoryEntries,
    syncHistoryEntries,
    storeHistoryEvent,
  };
}
